const { filterOptions } = require('./matching.js')

// closeReason records *why* the list is closed, so a second Escape press can
// tell "the user dismissed the list" apart from "the user just confirmed a
// selection" - without this, Escape after a confirmed choice would silently
// wipe out the value the user just picked.
function createInitialState() {
  return {
    isOpen: false,
    highlightedId: null,
    highlightCleared: false, // see onArrowUp - tells "never arrowed yet" apart from "arrowed up past the first option"
    closeReason: null, // 'dismissed' | 'confirmed' | null
    matches: [],
    matchCount: 0,
    status: 'belowThreshold',
    lastConfirmedValue: null
  }
}

// Re-filters as the user types. Highlight always resets on re-filter: the
// list has conceptually changed, so nothing should stay "current" that the
// user didn't just choose themselves.
function onType(state, { query, options, mode, minChars }) {
  const { status, matches, matchCount } = filterOptions({
    options,
    query,
    mode,
    minChars
  })
  return Object.assign({}, state, {
    isOpen: status !== 'belowThreshold',
    highlightedId: null,
    highlightCleared: false,
    closeReason: null,
    // Typing over a confirmed value abandons it, so a later second Escape
    // may clear the text again.
    lastConfirmedValue: null,
    status,
    matches,
    matchCount
  })
}

function highlightIndex(state, index) {
  const option = state.matches[index]
  return Object.assign({}, state, {
    isOpen: true,
    highlightedId: option.id,
    highlightCleared: false,
    closeReason: null,
    result: { type: 'highlighted', option }
  })
}

// The "set list" context's expand button shows every option, bypassing the
// character threshold entirely - this is the down arrow's keyboard
// equivalent to that button, for the one context where it exists. The
// adapter must only call this (instead of onArrowDown) when the combobox has
// no match yet *and* its context is "set list" - address lookup and search
// have no "browse everything" affordance for a mouse user either, so there's
// no keyboard parity gap to fix for them.
function onRequestFullList(state, { options, highlightFirst = true }) {
  if (options.length === 0) {
    return Object.assign({}, state, { result: { type: 'unhandled' } })
  }
  const opened = Object.assign({}, state, {
    isOpen: true,
    closeReason: null,
    status: 'hasMatches',
    matches: options,
    matchCount: options.length,
    highlightedId: null
  })
  if (!highlightFirst) {
    // A mouse click on the expand button opens the list with focus staying
    // on the input, nothing pre-highlighted - highlightCleared must still be
    // reset here (not just by highlightIndex below), or a stale `true` left
    // over from an earlier ArrowUp-past-the-first-option sequence would
    // wrongly suppress ArrowUp's jump-to-last-option on this freshly opened
    // list.
    return Object.assign({}, opened, {
      highlightCleared: false,
      result: { type: 'opened' }
    })
  }
  return highlightIndex(opened, 0)
}

function onArrowDown(state) {
  if (state.matchCount === 0) {
    return Object.assign({}, state, { result: { type: 'unhandled' } })
  }
  if (state.highlightedId === null) {
    return highlightIndex(state, 0)
  }
  const currentIndex = state.matches.findIndex(
    (option) => option.id === state.highlightedId
  )
  const nextIndex = Math.min(currentIndex + 1, state.matches.length - 1)
  return highlightIndex(state, nextIndex)
}

function onArrowUp(state) {
  if (state.matchCount === 0) {
    return Object.assign({}, state, { result: { type: 'unhandled' } })
  }
  if (state.highlightedId === null) {
    if (state.highlightCleared) {
      // Already stepped up past the first option - stay there rather than
      // wrapping around to the last option again (no wrap, matches the
      // WAI-ARIA list-autocomplete pattern).
      return Object.assign({}, state, {
        result: { type: 'highlighted', option: null }
      })
    }
    return highlightIndex(state, state.matches.length - 1)
  }
  const currentIndex = state.matches.findIndex(
    (option) => option.id === state.highlightedId
  )
  if (currentIndex === 0) {
    return Object.assign({}, state, {
      highlightedId: null,
      highlightCleared: true,
      result: { type: 'highlighted', option: null }
    })
  }
  return highlightIndex(state, currentIndex - 1)
}

// No-ops (rather than confirming nothing) when no option is highlighted, so
// a context where Enter submits the form - search - still gets a normal
// Enter keypress to work with.
function onEnter(state) {
  if (!state.isOpen || state.highlightedId === null) {
    return Object.assign({}, state, { result: { type: 'noop' } })
  }
  const option = state.matches.find(
    (candidate) => candidate.id === state.highlightedId
  )
  return Object.assign({}, state, {
    isOpen: false,
    highlightedId: null,
    closeReason: 'confirmed',
    lastConfirmedValue: option.label,
    result: { type: 'confirmed', option }
  })
}

// Confirms whichever option was clicked, not just the keyboard-highlighted
// one - a mouse user can click any visible option directly, so this mirrors
// onEnter's confirm branch without requiring it to already be highlighted.
function onOptionClick(state, { option }) {
  return Object.assign({}, state, {
    isOpen: false,
    highlightedId: null,
    highlightCleared: false,
    closeReason: 'confirmed',
    lastConfirmedValue: option.label,
    result: { type: 'confirmed', option }
  })
}

// Typing an option's text and leaving the field (or submitting) accepts it,
// the same as choosing it from the list - recorded as confirmed so Escape
// treats it like any other confirmed choice.
function onAcceptMatch(state, { option }) {
  return Object.assign({}, state, {
    isOpen: false,
    highlightedId: null,
    highlightCleared: false,
    closeReason: 'confirmed',
    lastConfirmedValue: option.label
  })
}

// First Escape: closes the list, focus stays put. Second Escape: clears the
// text, but only if the list is closed because it was dismissed and the text
// was never confirmed - checking closeReason alone isn't enough, since
// re-opening a confirmed value (ArrowDown) and dismissing it again would
// otherwise let the next Escape wipe out the confirmed choice.
function onEscape(state) {
  if (state.isOpen) {
    return Object.assign({}, state, {
      isOpen: false,
      highlightedId: null,
      highlightCleared: false,
      closeReason: 'dismissed',
      result: { type: 'closed' }
    })
  }
  if (state.closeReason === 'dismissed' && state.lastConfirmedValue === null) {
    // Same filter state as typing into an empty field, so nothing from the
    // abandoned query (matches, count) survives into the next ArrowDown.
    return Object.assign({}, state, {
      closeReason: null,
      status: 'belowThreshold',
      matches: [],
      matchCount: 0,
      result: { type: 'cleared' }
    })
  }
  return Object.assign({}, state, { result: { type: 'noop' } })
}

// Home, End, and anything else this component doesn't own. The adapter must
// not call preventDefault() on an 'unhandled' result, so native behaviour
// (e.g. text-cursor movement) keeps working.
function onUnhandledKey(state) {
  return Object.assign({}, state, { result: { type: 'unhandled' } })
}

module.exports = {
  createInitialState,
  onType,
  onRequestFullList,
  onArrowDown,
  onArrowUp,
  onEnter,
  onOptionClick,
  onAcceptMatch,
  onEscape,
  onUnhandledKey
}
